import React from 'react';
import {
    ArCustomerDetailResponse,
    ArLedgerDetailResponse,
    ArLedgerRecord,
    ArCustomerRecord,
} from '../../../api/types';
import formatCurrency from '../../../utils/formatCurrency';
import { DrawerBadge, DrawerField } from '../shared/LedgerDrawerPrimitives';

type ArCustomerDetailInvoice = NonNullable<ArCustomerDetailResponse['invoices']>[number];
type ArCustomerDetailPayment = NonNullable<ArCustomerDetailResponse['qbo_payments']>[number];
type ArCustomerDetailBankReceipt = NonNullable<NonNullable<ArCustomerDetailResponse['bank_receipt_coverage']>['linked_bank_receipts']>[number];
type ArCustomerDetailCashApplication = NonNullable<ArCustomerDetailResponse['cash_applications']>[number];

type ArCustomerInvoiceLinkRow = {
    invoice: ArCustomerDetailInvoice;
    invoiceId: string;
    invoiceNumber: string;
    matchedPayments: ArCustomerDetailPayment[];
    bankReceipts: ArCustomerDetailBankReceipt[];
    applications: ArCustomerDetailCashApplication[];
    allocatedTotal: number;
    evidenceTotal: number;
};

type DrawerPresentation = {
    title: string;
    subtitle: string;
    amountDisplay: React.ReactNode;
    metadataContent: React.ReactNode;
};

export const buildArLedgerDrawerPresentation = ({
    ar,
    detail,
    isArLedgerDetailLoading,
    hasArManualOverride,
    canManualLink,
    showAllocationAction,
    evidenceButtonLabel,
    allocationButtonLabel,
    onOpenManualLink,
    onRemoveManualOverride,
    removeManualLinkPending,
    formatDate,
    formatPercent,
    formatTitleCase,
    compactStatusTone,
    formatConfidencePercent,
    renderArCustomerLink,
    renderArInvoiceLink,
    renderPaymentLink,
    renderBankReceiptLink,
    renderQboTransactionLink,
    getFriendlyPaymentLabel,
    getFriendlyBankReceiptLabel,
    getFriendlyBankReceiptStatus,
    getCreditMemoSummary,
    getPaymentSourceSummary,
    isNonBankLinkedPayment,
    describeArException,
}: {
    ar: ArLedgerRecord;
    detail?: ArLedgerDetailResponse;
    isArLedgerDetailLoading: boolean;
    hasArManualOverride: boolean;
    canManualLink: boolean;
    showAllocationAction: boolean;
    evidenceButtonLabel: string;
    allocationButtonLabel: string;
    onOpenManualLink: () => void;
    onRemoveManualOverride: () => void;
    removeManualLinkPending: boolean;
    formatDate: (value?: string) => string;
    formatPercent: (value?: number | null, decimals?: number) => string;
    formatTitleCase: (value?: string | null) => string;
    compactStatusTone: (status?: string | null) => string;
    formatConfidencePercent: (value?: number | null) => string;
    renderArCustomerLink: (customerId?: string | null, customerName?: string | null) => React.ReactNode;
    renderArInvoiceLink: (invoiceId?: string | null, invoiceNumber?: string | null, fallbackLabel?: string | null) => React.ReactNode;
    renderPaymentLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    renderBankReceiptLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    renderQboTransactionLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    getFriendlyPaymentLabel: (payment: NonNullable<ArLedgerDetailResponse['linked_payments']>[number] | undefined, customerName?: string | null) => string;
    getFriendlyBankReceiptLabel: (receipt: NonNullable<ArLedgerDetailResponse['linked_bank_receipts']>[number] | undefined, customerName?: string | null) => string;
    getFriendlyBankReceiptStatus: (receipt: NonNullable<ArLedgerDetailResponse['linked_bank_receipts']>[number] | undefined) => string;
    getCreditMemoSummary: (payment: NonNullable<ArLedgerDetailResponse['linked_payments']>[number] | undefined) => string;
    getPaymentSourceSummary: (hasQboPayment: boolean, hasBankReceipt: boolean) => string;
    isNonBankLinkedPayment: (
        linkedPaymentId: string | null | undefined,
        linkedPayments: Array<NonNullable<ArLedgerDetailResponse['linked_payments']>[number]>
    ) => boolean;
    describeArException: (
        exception: NonNullable<ArLedgerDetailResponse['linked_exceptions']>[number] | undefined,
        linkedInvoices: any[],
    ) => string;
}): DrawerPresentation => {
    const recordType = String(ar?.record_type || '').trim();
    const reconciliationStatus = String(ar?.reconciliation_status || '').trim();
    const isPayment = recordType === 'payment';
    const isInvoice = recordType === 'invoice';
    const confidenceScore = ar?.confidence_score ?? 0;
    const paymentEvidenceSources = (ar?.payment_evidence_sources || []).filter(Boolean);
    const linkedInvoices = [...(detail?.linked_invoices || [])];
    const linkedPayments = detail?.linked_payments || [];
    const linkedBankReceipts = detail?.linked_bank_receipts || [];
    const linkedCashApplications = detail?.linked_cash_applications || [];
    const linkedCreditApplications = detail?.linked_credit_applications || [];
    const linkedExceptions = detail?.linked_exceptions || [];
    const genericLowConfidenceExceptions = linkedExceptions.filter((exception) => String(exception.exception_type || '').trim() === 'low_confidence_generic_counterparty');
    const hasEvidence = linkedPayments.length > 0 || linkedBankReceipts.length > 0;
    const hasLowConfidenceSuggestedEvidence = genericLowConfidenceExceptions.length > 0 && !hasEvidence;
    const hasApplications = linkedCashApplications.length > 0;
    const arFriendlyReference = isPayment
        ? (ar?.reference_no || getFriendlyPaymentLabel(linkedPayments[0], ar?.customer_name))
        : (ar?.reference_no || 'Invoice');
    const evidenceSummary = getPaymentSourceSummary(linkedPayments.length > 0, linkedBankReceipts.length > 0);

    return {
        title: arFriendlyReference || ar?.record_type || 'AR Ledger Record',
        subtitle: ar?.customer_name || 'AR Ledger',
        amountDisplay: (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(ar?.amount, 'USD')}
                </span>
                <DrawerBadge className="bg-cyan-100 text-cyan-800">{formatTitleCase(recordType)}</DrawerBadge>
                {isPayment && paymentEvidenceSources.map((source) => (
                    <DrawerBadge key={source} className="bg-indigo-100 text-indigo-800">{formatTitleCase(source)}</DrawerBadge>
                ))}
                {isPayment && typeof ar?.applied_amount === 'number' && ar.applied_amount > 0 && (
                    <DrawerBadge className="bg-emerald-100 text-emerald-800">
                        Applied {formatCurrency(ar.applied_amount, 'USD')}
                    </DrawerBadge>
                )}
            </div>
        ),
        metadataContent: (
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <DrawerField label="Type" value={formatTitleCase(ar?.record_type)} />
                    <DrawerField label="Customer" value={renderArCustomerLink(ar?.customer_id, ar?.customer_name)} />
                    <DrawerField label={isPayment ? 'Applied Invoice' : 'Reference'} value={arFriendlyReference || '—'} />
                    <DrawerField label="Amount" value={formatCurrency(ar?.amount, 'USD')} />
                    <DrawerField label="Date" value={formatDate(ar?.event_date)} />
                    <DrawerField label="Due Date" value={formatDate(ar?.due_date || undefined)} />
                    <DrawerField label={isPayment ? 'Unapplied Amount' : 'Open Amount'} value={formatCurrency(ar?.open_amount, 'USD')} />
                    <DrawerField label="Confidence" value={formatConfidencePercent(confidenceScore)} />
                </div>

                <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{isPayment ? 'Invoice Link' : 'Linked Records'}</h3>
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                        {isArLedgerDetailLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                Loading linked records...
                            </div>
                        ) : (
                            <div className="space-y-4 text-sm text-slate-700">
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                                        <span>{isInvoice ? 'Payment evidence' : 'Evidence'} {evidenceSummary}</span>
                                        <span>{isInvoice ? 'Applied payments' : 'Applications'} {hasApplications ? linkedCashApplications.length : 0}</span>
                                        <span>Reconciliation {formatTitleCase(reconciliationStatus)}</span>
                                        <span>Confidence {formatPercent(confidenceScore, 0)}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {canManualLink && (
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md border border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                                                onClick={onOpenManualLink}
                                            >
                                                {evidenceButtonLabel}
                                            </button>
                                        )}
                                        {showAllocationAction && (
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md border border-emerald-300 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                                                onClick={onOpenManualLink}
                                            >
                                                {allocationButtonLabel}
                                            </button>
                                        )}
                                        {hasArManualOverride && (
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                                                onClick={onRemoveManualOverride}
                                                disabled={removeManualLinkPending}
                                            >
                                                {removeManualLinkPending ? 'Removing...' : 'Undo Manual Override'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {!!genericLowConfidenceExceptions.length && (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                        <div>
                                            <span className="font-medium text-amber-900">Suggested low-confidence bank evidence:</span>{' '}
                                            {describeArException(genericLowConfidenceExceptions[0], linkedInvoices)}
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md border border-amber-300 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
                                                onClick={onOpenManualLink}
                                            >
                                                Review Suggested Evidence
                                            </button>
                                            <span className="text-xs text-amber-700 self-center">
                                                Confirm if this amount-only bank match looks right, or use manual override to choose a different trail.
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {!!linkedInvoices.length && (
                                    <div>
                                        <span className="font-medium text-slate-900">{isPayment ? 'Linked invoices:' : 'Invoice references:'}</span>
                                        {linkedInvoices.map((invoice, index) => (
                                            <div key={invoice._id || invoice.invoice_id || `linked-invoice-${index}`} className="mt-1">
                                                {index + 1}. {renderArInvoiceLink(invoice.invoice_id || invoice.source_id, invoice.invoice_number, invoice.invoice_number || 'Invoice')} | Due {formatDate(invoice.due_date || undefined)} | Open {formatCurrency(invoice.open_amount_finz ?? invoice.open_amount, 'USD')}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!!linkedPayments.length && (
                                    <div>
                                        <span className="font-medium text-slate-900">{isPayment ? 'Payment evidence:' : 'Applied payments:'}</span>
                                        {linkedPayments.map((payment, index) => {
                                            const paymentId = String(payment.source_id || '').trim();
                                            const hasMatchedBankReceipt = paymentId
                                                ? linkedBankReceipts.some(
                                                    (receipt) => String(receipt.linked_payment_id || '').trim() === paymentId
                                                        && String((payment as Record<string, unknown>).source_system || '').trim().toLowerCase() !== 'bank'
                                                )
                                                : false;
                                            return (
                                                <div key={payment.source_id || `linked-payment-${index}`} className="mt-1 flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        {index + 1}. {renderPaymentLink(payment.source_id, getFriendlyPaymentLabel(payment, ar?.customer_name))} | {formatDate(payment.payment_date || undefined)} | {formatCurrency(payment.payment_amount, 'USD')} | {payment.payment_kind === 'credit_application' ? 'Credit applied' : formatTitleCase(payment.status)} | {payment.payment_kind === 'credit_application' ? 'Credit memo + invoice' : (hasMatchedBankReceipt ? 'QBO + bank matched' : 'QBO only')}
                                                        {!!getCreditMemoSummary(payment) && (
                                                            <div className="mt-1 text-sky-700">{getCreditMemoSummary(payment)}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {!!linkedBankReceipts.length && (
                                    <div>
                                        <span className="font-medium text-slate-900">{isInvoice ? 'Bank-side evidence:' : 'Bank evidence:'}</span>
                                        {linkedBankReceipts.map((receipt, index) => (
                                            <div key={receipt.source_id || `linked-bank-receipt-${index}`} className="mt-1">
                                                {index + 1}. {renderBankReceiptLink(receipt.source_id, getFriendlyBankReceiptLabel(receipt, ar?.customer_name))} | {formatDate(receipt.receipt_date || undefined)} | {formatCurrency(receipt.receipt_amount, 'USD')} | {getFriendlyBankReceiptStatus(receipt)} | {isNonBankLinkedPayment(receipt.linked_payment_id, linkedPayments) ? 'QBO + bank matched' : 'Bank only'}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!!linkedCashApplications.length && (
                                    <div>
                                        <span className="font-medium text-slate-900">{isInvoice ? 'Cash applications:' : 'Applications:'}</span>
                                        {linkedCashApplications.map((application, index) => (
                                            <div key={application.ar_cash_application_id || `linked-cash-application-${index}`} className="mt-1">
                                                {index + 1}. {renderArInvoiceLink(
                                                    application.invoice_id,
                                                    linkedInvoices.find((invoice) => invoice.invoice_id === application.invoice_id || invoice.source_id === application.invoice_id)?.invoice_number,
                                                    'Allocation'
                                                )} | Applied {formatCurrency(application.allocated_amount, 'USD')} | {formatTitleCase(application.status || application.allocation_type)}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!!linkedCreditApplications.length && (
                                    <div>
                                        <span className="font-medium text-slate-900">Credit applications:</span>
                                        {linkedCreditApplications.map((application, index) => {
                                            const invoiceNumber = linkedInvoices.find(
                                                (invoice) => invoice.invoice_id === application.invoice_id || invoice.source_id === application.invoice_id
                                            )?.invoice_number;
                                            const creditMemoNumber = application.credit_memo_numbers?.[0];
                                            const creditMemoId = String(application.credit_memo_id || application.credit_memo_ids?.[0] || '').trim();
                                            return (
                                                <div key={application.ar_credit_application_id || `linked-credit-application-${index}`} className="mt-1">
                                                    {index + 1}. {creditMemoId
                                                        ? renderQboTransactionLink(creditMemoId, creditMemoNumber || `Credit memo ${creditMemoId}`)
                                                        : (creditMemoNumber || 'Credit memo')
                                                    } | {renderArInvoiceLink(
                                                        application.invoice_id,
                                                        invoiceNumber,
                                                        invoiceNumber || 'Invoice'
                                                    )} | Applied {formatCurrency(application.applied_amount, 'USD')} | {formatTitleCase(application.source_kind || application.status || 'detected')}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {!linkedInvoices.length && !linkedPayments.length && !linkedBankReceipts.length && !linkedCashApplications.length && !linkedCreditApplications.length && !linkedExceptions.length && (
                                    <p className="text-sm text-gray-600">
                                        {isPayment ? 'No invoice or bank evidence linked to this payment yet.' : 'No linked records found for this ledger row.'}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        ),
    };
};

export const buildArCustomerDrawerPresentation = ({
    customer,
    detail,
    invoiceLinkRows,
    expandedCustomerInvoiceIds,
    setExpandedCustomerInvoiceIds,
    formatDate,
    formatPercent,
    getArInvoiceStatus,
    getArInvoiceOpenAmount,
    getArInvoicePaidAmount,
    getArInvoiceCreditedAmount,
    compactStatusTone,
    renderArInvoiceLink,
    renderPaymentLink,
    renderBankReceiptLink,
    getFriendlyPaymentLabel,
    getFriendlyBankReceiptLabel,
    getCreditMemoSummary,
}: {
    customer: ArCustomerRecord;
    detail?: ArCustomerDetailResponse;
    invoiceLinkRows: ArCustomerInvoiceLinkRow[];
    expandedCustomerInvoiceIds: string[];
    setExpandedCustomerInvoiceIds: React.Dispatch<React.SetStateAction<string[]>>;
    formatDate: (value?: string) => string;
    formatPercent: (value?: number | null, decimals?: number) => string;
    getArInvoiceStatus: (invoice?: ArCustomerDetailInvoice | null) => string;
    getArInvoiceOpenAmount: (invoice?: ArCustomerDetailInvoice | null) => number;
    getArInvoicePaidAmount: (invoice?: ArCustomerDetailInvoice | null) => number;
    getArInvoiceCreditedAmount: (invoice?: ArCustomerDetailInvoice | null) => number;
    compactStatusTone: (status?: string | null) => string;
    renderArInvoiceLink: (invoiceId?: string | null, invoiceNumber?: string | null, fallbackLabel?: string | null) => React.ReactNode;
    renderPaymentLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    renderBankReceiptLink: (transactionId?: string | null, label?: string | null) => React.ReactNode;
    getFriendlyPaymentLabel: (payment: ArCustomerDetailPayment | undefined, customerName?: string | null) => string;
    getFriendlyBankReceiptLabel: (receipt: ArCustomerDetailBankReceipt | undefined, customerName?: string | null) => string;
    getCreditMemoSummary: (payment: ArCustomerDetailPayment | undefined) => string;
}): DrawerPresentation => {
    const summary = detail?.summary;

    return {
        title: detail?.customer_name || customer?.customer_name || 'Customer',
        subtitle: 'AR Customer',
        amountDisplay: (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(summary?.open_ar_total ?? customer?.open_ar, 'USD')}
                </span>
                <DrawerBadge className="bg-cyan-100 text-cyan-800">AR Customer</DrawerBadge>
                <DrawerBadge className="bg-slate-100 text-slate-700">
                    {summary?.open_invoice_count ?? customer?.open_invoice_count ?? 0} open invoice{(summary?.open_invoice_count ?? customer?.open_invoice_count ?? 0) === 1 ? '' : 's'}
                </DrawerBadge>
            </div>
        ),
        metadataContent: (
            <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <DrawerField label="Total Invoiced" value={formatCurrency(summary?.total_invoiced_amount ?? customer?.total_invoiced_amount, 'USD')} />
                    <DrawerField label="Cash Collected" value={formatCurrency(summary?.paid_amount_total ?? customer?.paid_amount_total, 'USD')} />
                    <DrawerField label="Open AR" value={formatCurrency(summary?.open_ar_total ?? customer?.open_ar, 'USD')} />
                    <DrawerField label="Overdue AR" value={formatCurrency(summary?.overdue_ar_total ?? customer?.overdue_ar, 'USD')} />
                    <DrawerField label="Total Invoices" value={summary?.total_invoice_count ?? customer?.total_invoice_count ?? '—'} />
                    <DrawerField label="Partial Invoices" value={summary?.partial_invoice_count ?? customer?.partial_invoice_count ?? '—'} />
                    <DrawerField label="Partial 30d" value={summary?.partial_payment_count_30d ?? customer?.partial_payment_frequency ?? 0} />
                    <DrawerField label="Last Payment" value={formatDate((summary?.last_payment_date ?? customer?.last_payment_date) || undefined)} />
                    <DrawerField label="Avg Days To Pay" value={summary?.avg_days_to_pay ?? customer?.avg_days_to_pay ?? '—'} />
                    <DrawerField label="Payment Drift" value={summary?.payment_drift_days ?? customer?.payment_drift_days ?? '—'} />
                    <DrawerField label="Risk" value={summary?.collection_risk_level || customer?.collection_risk_level || '—'} />
                    <DrawerField label="Concentration" value={formatPercent(summary?.concentration_share ?? customer?.concentration_share)} />
                </div>

                <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Invoice Link</h3>
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                        {!!invoiceLinkRows.length ? (
                            <div className="space-y-4">
                                {invoiceLinkRows.map((row) => {
                                    const isExpanded = expandedCustomerInvoiceIds.includes(row.invoiceId);
                                    return (
                                        <div key={row.invoiceId || row.invoiceNumber} className="border-b border-slate-200 pb-4 last:border-b-0 last:pb-0">
                                            <button
                                                type="button"
                                                className="flex w-full items-start justify-between gap-3 text-left"
                                                onClick={() => {
                                                    setExpandedCustomerInvoiceIds((current) => (
                                                        current.includes(row.invoiceId)
                                                            ? current.filter((invoiceId) => invoiceId !== row.invoiceId)
                                                            : [...current, row.invoiceId]
                                                    ));
                                                }}
                                            >
                                                <div className="min-w-0 flex-1 space-y-1">
                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                                        <span className="text-base font-semibold text-slate-900">
                                                            {renderArInvoiceLink(row.invoiceId, row.invoice.invoice_number, row.invoiceNumber)}
                                                        </span>
                                                        <DrawerBadge className={compactStatusTone(getArInvoiceStatus(row.invoice))}>
                                                            {getArInvoiceStatus(row.invoice)}
                                                        </DrawerBadge>
                                                        <span className="text-sm text-slate-500">Due {formatDate(row.invoice.due_date || undefined)}</span>
                                                        {row.invoice.partial_payment_detected && (
                                                            <span className="text-sm text-amber-700">Partial payment detected</span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                                                        <span>Original {formatCurrency(row.invoice.original_amount, 'USD')}</span>
                                                        <span>Open {formatCurrency(getArInvoiceOpenAmount(row.invoice), 'USD')}</span>
                                                        <span>Cash Collected {formatCurrency(getArInvoicePaidAmount(row.invoice), 'USD')}</span>
                                                        {getArInvoiceCreditedAmount(row.invoice) > 0 && (
                                                            <span>Credited {formatCurrency(getArInvoiceCreditedAmount(row.invoice), 'USD')}</span>
                                                        )}
                                                        <span>Allocated {formatCurrency(row.allocatedTotal, 'USD')}</span>
                                                        <span>Evidence {formatCurrency(row.evidenceTotal, 'USD')}</span>
                                                    </div>
                                                </div>
                                                <span className="mt-1 shrink-0 text-slate-500">{isExpanded ? '▾' : '▸'}</span>
                                            </button>

                                            {isExpanded && (
                                                <div className="mt-3 space-y-2 text-sm text-slate-700">
                                                    <div>
                                                        <span className="font-medium text-slate-900">Linked payments:</span>{' '}
                                                        {row.matchedPayments.length ? (
                                                            row.matchedPayments.map((payment, index) => (
                                                                <div key={payment.source_id || `${row.invoiceId}-payment`} className="mt-1">
                                                                    {index + 1}. {renderPaymentLink(payment.source_id, getFriendlyPaymentLabel(payment, detail?.customer_name || customer?.customer_name))} | {formatDate(payment.payment_date || undefined)} | {formatCurrency(payment.payment_amount, 'USD')} | {payment.payment_kind === 'credit_application' ? 'credit applied' : (payment.bank_matched ? 'bank matched' : 'no bank match')}
                                                                    {!!getCreditMemoSummary(payment) && (
                                                                        <div className="ml-4 mt-1 text-sky-700">
                                                                            {getCreditMemoSummary(payment)}
                                                                        </div>
                                                                    )}
                                                                    {!!payment.linked_bank_receipt_ids?.length && (
                                                                        <div className="ml-4 mt-1 text-slate-500">
                                                                            Bank receipts: {payment.linked_bank_receipt_ids.map((receiptId, receiptIndex) => {
                                                                                const receipt = row.bankReceipts.find((candidate) => String(candidate.source_id || '').trim() === String(receiptId || '').trim());
                                                                                return (
                                                                                    <React.Fragment key={receiptId}>
                                                                                        {receiptIndex > 0 ? ', ' : ''}
                                                                                        {renderBankReceiptLink(receiptId, getFriendlyBankReceiptLabel(receipt, detail?.customer_name || customer?.customer_name))}
                                                                                        {receipt ? ` ${formatCurrency(receipt.receipt_amount, 'USD')}` : ''}
                                                                                    </React.Fragment>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span className="text-slate-500">none</span>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <span className="font-medium text-slate-900">Linked bank deposits:</span>{' '}
                                                        {row.bankReceipts.filter((receipt) => !String(receipt.linked_payment_id || '').trim()).length ? (
                                                            row.bankReceipts
                                                                .filter((receipt) => !String(receipt.linked_payment_id || '').trim())
                                                                .map((receipt, index) => (
                                                                    <div key={receipt.source_id || `${row.invoiceId}-bank`} className="mt-1">
                                                                        {index + 1}. {renderBankReceiptLink(receipt.source_id, getFriendlyBankReceiptLabel(receipt, detail?.customer_name || customer?.customer_name))} | {formatDate(receipt.receipt_date || undefined)} | {formatCurrency(receipt.receipt_amount, 'USD')} | {receipt.status || 'unknown'}
                                                                    </div>
                                                                ))
                                                        ) : (
                                                            <span className="text-slate-500">none</span>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <span className="font-medium text-slate-900">Applications:</span>{' '}
                                                        {row.applications.length ? (
                                                            row.applications.map((application, index) => (
                                                                <div key={`${row.invoiceId}-application-${index}`} className="mt-1">
                                                                    {index + 1}. Applied {formatCurrency(application.allocated_amount, 'USD')} | {application.allocation_type || application.status || 'cash app'}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span className="text-slate-500">none</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">No invoice link records are currently attached to this customer.</p>
                        )}
                    </div>
                </div>
            </div>
        ),
    };
};
